/**
 * Portfolio Health Classification
 *
 * Classifies pieces relative to the survival floor (cents/hr).
 * Used by diagnostic tiles, piece cards, and recents row.
 */

import type { VerdictSummary } from '@/types';
import { isAboveFloor } from '@/lib/floorCheck';

export type HealthStatus = 'below' | 'near' | 'above' | 'sold';

export const HEALTH_LABELS: Record<HealthStatus, string> = {
  below: 'Below',
  near: 'Risk',
  above: 'Strong',
  sold: 'Sold',
};

/**
 * Classify a piece's pricing health relative to the survival floor.
 * Uses total-price comparison (gutPrice >= TTCP) via isAboveFloor(),
 * the single source of truth defined in floorCheck.ts.
 * All monetary values in CENTS.
 */
export function getHealth(item: VerdictSummary, floorHourly: number): HealthStatus {
  if (item.sold) return 'sold';

  // Primary check: total price vs TTCP floor (canonical definition)
  const askingPrice = item.gutPrice ?? item.ttcp;
  if (item.ttcp <= 0) return 'below';
  if (!isAboveFloor({ gutPrice: item.gutPrice, truePrice: item.ttcp, floor: item.ttcp })) return 'below';

  // Near zone: within 10% above floor
  const ratio = askingPrice / item.ttcp;
  if (ratio < 1.10) return 'near';
  return 'above';
}

export function healthColor(h: HealthStatus): string {
  const colors: Record<HealthStatus, string> = {
    below: '#E07A3D',
    near: '#C69026',
    above: '#3DABA5',
    sold: '#8E8B87',
  };
  return colors[h];
}

export function healthBg(h: HealthStatus): string {
  const bgs: Record<HealthStatus, string> = {
    below: 'rgba(224,122,61,0.08)',
    near: 'rgba(198,144,38,0.08)',
    above: 'rgba(61,171,165,0.08)',
    sold: 'rgba(142,139,135,0.08)',
  };
  return bgs[h];
}

export function healthBgActive(h: HealthStatus): string {
  const bgs: Record<HealthStatus, string> = {
    below: 'rgba(224,122,61,0.15)',
    near: 'rgba(198,144,38,0.15)',
    above: 'rgba(61,171,165,0.12)',
    sold: 'rgba(142,139,135,0.15)',
  };
  return bgs[h];
}

// ── Canonical Portfolio Health Score ──

export interface ScoredVerdict {
  isSold: boolean;
  price: number;  // soldPrice for sold, gutPrice for active (cents)
  floor: number;  // TTCP (cents)
}

export interface PortfolioHealthResult {
  score: number;
  above: number;
  below: number;
  total: number;
  activeAbove: number;
  activeBelow: number;
  soldAbove: number;
  soldBelow: number;
}

export const WEIGHT_ACTIVE = 1.0;
export const WEIGHT_SOLD_ABOVE = 0.5;
export const WEIGHT_SOLD_BELOW = 0.25;

export function computePortfolioHealth(verdicts: ScoredVerdict[]): PortfolioHealthResult {
  if (verdicts.length === 0) {
    return { score: 0, above: 0, below: 0, total: 0,
             activeAbove: 0, activeBelow: 0, soldAbove: 0, soldBelow: 0 };
  }

  let weightedAbove = 0;
  let weightedTotal = 0;
  let activeAbove = 0, activeBelow = 0;
  let soldAbove = 0, soldBelow = 0;

  for (const v of verdicts) {
    const aboveFloor = v.price >= v.floor;
    if (v.isSold) {
      if (aboveFloor) {
        weightedAbove += WEIGHT_SOLD_ABOVE;
        weightedTotal += WEIGHT_SOLD_ABOVE;
        soldAbove++;
      } else {
        weightedTotal += WEIGHT_SOLD_BELOW;
        soldBelow++;
      }
    } else {
      if (aboveFloor) {
        weightedAbove += WEIGHT_ACTIVE;
        activeAbove++;
      } else {
        activeBelow++;
      }
      weightedTotal += WEIGHT_ACTIVE;
    }
  }

  const score = weightedTotal > 0
    ? Math.max(0, Math.min(100, Math.round((weightedAbove / weightedTotal) * 100)))
    : 0;

  return {
    score,
    above: activeAbove + soldAbove,
    below: activeBelow + soldBelow,
    total: verdicts.length,
    activeAbove, activeBelow, soldAbove, soldBelow,
  };
}

export function toScoredVerdict(v: {
  sold?: boolean;
  soldPrice?: number | null;
  inputs?: { gutPrice?: number | null };
  calculations?: { ttcp?: number };
}): ScoredVerdict | null {
  const floor = v.calculations?.ttcp ?? 0;
  if (floor <= 0) return null;
  const isSold = v.sold === true;
  const price = isSold
    ? (v.soldPrice ?? v.inputs?.gutPrice ?? 0)
    : (v.inputs?.gutPrice ?? v.calculations?.ttcp ?? 0);
  return { isSold, price, floor };
}

/** Deterministic hue from string for avatar background colors */
export function avColor(title: string): string {
  let h = 0;
  for (let i = 0; i < title.length; i++) {
    h = title.charCodeAt(i) + ((h << 5) - h);
  }
  return `hsl(${Math.abs(h) % 360}, 35%, 72%)`;
}
